import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Sparkles, ArrowUpRight, ArrowDownRight, Loader2, SlidersHorizontal, ChevronDown, PenLine, FlaskConical } from "lucide-react";
import { MONTHS, formatCompact, formatNumber, CHANNEL_NAME_KEY } from "../utils";
import { computeFindingsSheet } from "../findings";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const YEARS = ["2024", "2025", "2026"];
const TONES = [
  { value: "direct", label: "Direct tone" },
  { value: "consultative", label: "Consultative tone" },
  { value: "formal", label: "Formal tone" },
];
const LENGTHS = [
  { value: "brief", label: "Brief" },
  { value: "detailed", label: "Detailed" },
];

const DeltaPill = ({ value, lowerIsBetter }) => {
  if (value === null || value === undefined) return <span className="text-xs text-gray-400 font-semibold">–</span>;
  const good = lowerIsBetter ? value < 0 : value >= 0;
  const Icon = value >= 0 ? ArrowUpRight : ArrowDownRight;
  return (
    <span className={`inline-flex items-center gap-0.5 px-2 py-0.5 rounded-full text-[11px] font-bold ${good ? "bg-[#E6F4EA] text-[#137333]" : "bg-[#FCE8E6] text-[#C5221F]"}`}>
      <Icon className="w-3 h-3" /> {Math.abs(value).toFixed(1)}%
    </span>
  );
};

export default function StepConfigure({ config, setConfig, kpis, channels, kpiCurrent, kpiPrevious, kpiLastYear, channelSets, ai, setAi, allChannels, channelPrefs, setChannelPrefs, skills, goTo }) {
  const [generating, setGenerating] = useState(false);
  const [analyzing, setAnalyzing] = useState(false);
  const [instOpen, setInstOpen] = useState(false);
  const [editChannels, setEditChannels] = useState(false);

  // STAGE 1 — deterministic engine computes verified facts, Claude interprets them into findings
  const analyzeData = async () => {
    if (!kpiCurrent) { toast.error("No KPI data found for the selected month"); return ""; }
    setAnalyzing(true);
    try {
      const sheet = computeFindingsSheet({ kpiCurrent, kpiPrevious, kpiLastYear, channelSets, config });
      const res = await axios.post(`${API}/generate-content`, {
        section: "data_findings",
        model: "claude",
        tone: "direct",
        length: "detailed",
        format_style: "bullets",
        currency: config.currency,
        context: sheet,
      });
      if (res.data.success && res.data.content) {
        setAi((prev) => ({ ...prev, findings: res.data.content }));
        toast.success("Data findings ready — review or edit before writing the narrative");
        return res.data.content;
      }
      toast.error(res.data.error || "Analysis failed — try again");
      return "";
    } catch {
      toast.error("Analysis failed — try again");
      return "";
    } finally {
      setAnalyzing(false);
    }
  };

  // STAGE 2 — Claude writes each section from: approved findings + shared instructions + per-section context
  const generateNarrative = async () => {
    if (!kpiCurrent) { toast.error("No KPI data found for the selected month"); return false; }
    let findings = ai.findings.trim();
    if (!findings) {
      toast.info("Analyzing the data first…");
      findings = (await analyzeData()).trim();
      if (!findings) return false;
    }
    setGenerating(true);
    try {
      // Respect channel selections — excluded channels are not shown to the LLM either
      const filterCh = (rows) =>
        rows ? rows.filter((r) => channelPrefs[(r[CHANNEL_NAME_KEY] || "").trim()]?.included !== false) : rows;
      const baseCtx = `${config.month} ${config.year} monthly report${config.country ? ` for ${config.country}` : ""}.\n\nVERIFIED DATA FINDINGS (pre-computed and analyst-approved — base your narrative on these, do not contradict them):\n${findings}`;
      const base = {
        model: "claude",
        currency: config.currency,
        kpi_data: kpiCurrent,
        kpi_previous: kpiPrevious,
        kpi_last_year: kpiLastYear,
        channel_data: filterCh(channelSets.current),
        channel_previous: filterCh(channelSets.previous),
        channel_last_year: filterCh(channelSets.lastYear),
        custom_instructions: ai.instructions || "",
        skill_name: ai.skillName || "Custom",
      };
      const [sumRes, insRes] = await Promise.all([
        axios.post(`${API}/generate-content`, {
          ...base, section: "executive_summary", tone: ai.tone, length: ai.length, format_style: "paragraph",
          context: `${baseCtx}${ai.summaryContext.trim() ? `\n\nANALYST CONTEXT FOR THIS SECTION (weave in): ${ai.summaryContext.trim()}` : ""}`,
        }),
        axios.post(`${API}/generate-content`, {
          ...base, section: "strategic_insights", tone: ai.tone, length: ai.length, format_style: "bullets",
          context: `${baseCtx}${ai.insightsContext.trim() ? `\n\nANALYST CONTEXT FOR THIS SECTION (weave in): ${ai.insightsContext.trim()}` : ""}`,
        }),
      ]);
      const summary = sumRes.data.success ? sumRes.data.content : "";
      const insights = insRes.data.success ? insRes.data.content : "";
      if (!summary && !insights) {
        const errMsg = sumRes.data.error || insRes.data.error || "AI generation failed — try again";
        console.error("[v1] Narrative generation failed:", { summaryRes: sumRes.data, insightsRes: insRes.data });
        toast.error(errMsg);
        return false;
      }
      if (!summary) {
        console.warn("[v1] Executive summary empty:", sumRes.data);
        toast.error(`Summary failed: ${sumRes.data.error || "empty response"}`);
      }
      if (!insights) {
        console.warn("[v1] Strategic insights empty:", insRes.data);
        toast.error(`Insights failed: ${insRes.data.error || "empty response"}`);
      }
      setAi((prev) => ({ ...prev, summary: summary || prev.summary, insights: insights || prev.insights }));
      if (summary && insights) toast.success("Narrative generated — review & edit below");
      return true;
    } catch (e) {
      const serverDetail = e?.response?.data?.detail || e?.response?.data?.error || e?.message || "unknown error";
      console.error("[v1] Narrative generation threw:", e?.response?.data || e);
      toast.error(`AI generation failed: ${typeof serverDetail === "string" ? serverDetail : JSON.stringify(serverDetail).slice(0, 200)}`);
      return false;
    } finally {
      setGenerating(false);
    }
  };

  // If the user continues without any narrative, run the full pipeline for them by default
  const handleContinue = async () => {
    if (!ai.summary.trim() && !ai.insights.trim() && kpiCurrent) {
      toast.info("No narrative yet — running the analysis for you…");
      await generateNarrative();
    }
    goTo(2);
  };

  const set = (k) => (e) => setConfig({ ...config, [k]: e.target.value });
  const selectCls = "border border-gray-300 rounded-full px-4 py-2 text-sm font-semibold bg-white focus:outline-none focus:border-green-700";

  return (
    <div className="max-w-4xl mx-auto">
      <h1 className="text-3xl sm:text-4xl font-extrabold tracking-tight text-center">Review your numbers</h1>
      <p className="text-center text-gray-500 mt-3 mb-8 text-sm sm:text-base">Confirm the report period, check the metrics, then let AI write the story.</p>

      {/* Config row */}
      <div className="v1-card p-5 flex flex-wrap items-center gap-3 justify-center" data-testid="v1-config-row">
        <select value={config.month} onChange={set("month")} className={selectCls} data-testid="v1-month-select">
          {MONTHS.map((m) => <option key={m} value={m}>{m}</option>)}
        </select>
        <select value={config.year} onChange={set("year")} className={selectCls} data-testid="v1-year-select">
          {YEARS.map((y) => <option key={y} value={y}>{y}</option>)}
        </select>
        <input value={config.country} onChange={set("country")} placeholder="Market (e.g. Italy)" className={`${selectCls} w-44`} data-testid="v1-country-input" />
        <select value={config.currency} onChange={set("currency")} className={selectCls} data-testid="v1-currency-select">
          <option value="EUR">€ EUR</option><option value="USD">$ USD</option><option value="GBP">£ GBP</option>
        </select>
      </div>

      {/* KPI cards */}
      {!kpiCurrent ? (
        <div className="mt-6 p-6 rounded-xl border border-amber-300 bg-amber-50 text-sm text-amber-800 text-center" data-testid="v1-no-month-warning">
          No KPI row found for <b>{config.month} {config.year}</b>. Pick a different month above or upload more data.
        </div>
      ) : (
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3 mt-6" data-testid="v1-kpi-grid">
          {kpis.map((k) => (
            <div key={k.key} className="v1-card p-4 text-center" data-testid={`v1-kpi-card-${k.key.replace(/\s+/g, "-").toLowerCase()}`}>
              <div className="text-[11px] font-bold text-gray-500 uppercase tracking-wide">{k.label}</div>
              <div className="text-2xl font-extrabold mt-2 tracking-tight">{k.display}</div>
              <div className="mt-2"><DeltaPill value={k.mom} lowerIsBetter={k.lowerIsBetter} /></div>
              {k.yoy !== null && <div className="text-[10px] text-gray-500 mt-1.5">YoY {k.yoy >= 0 ? "+" : ""}{k.yoy.toFixed(1)}%</div>}
            </div>
          ))}
        </div>
      )}

      {/* Channel table */}
      {allChannels.length > 0 && (
        <div className="v1-card mt-6 overflow-hidden" data-testid="v1-channel-table">
          <div className="px-5 py-4 border-b border-gray-100 flex items-center justify-between">
            <span className="font-extrabold">Channel performance</span>
            <button
              onClick={() => setEditChannels(!editChannels)}
              className={`flex items-center gap-1.5 text-xs font-bold px-3 py-1.5 rounded-full border transition-colors ${editChannels ? "bg-[#FEF7E0] border-[#B06000] text-[#B06000]" : "border-gray-300 text-gray-500 hover:border-gray-500"}`}
              data-testid="v1-edit-channels-toggle"
            >
              <PenLine className="w-3 h-3" /> {editChannels ? "Done" : "Select / rename"}
            </button>
          </div>
          {editChannels ? (
            <div className="p-4 space-y-2" data-testid="v1-channel-edit-panel">
              <div className="text-xs text-gray-500 mb-3">Untick channels to leave them out of the report. Rename to change how they appear in the newsletter.</div>
              {allChannels.map((c) => {
                const pref = channelPrefs[c.name] || {};
                const included = pref.included !== false;
                return (
                  <div key={c.name} className={`flex items-center gap-3 px-3 py-2 rounded-xl border ${included ? "border-gray-200" : "border-gray-100 opacity-50"}`} data-testid={`v1-channel-edit-row-${c.name.replace(/\s+/g, "-").toLowerCase()}`}>
                    <input
                      type="checkbox"
                      checked={included}
                      onChange={(e) => setChannelPrefs({ ...channelPrefs, [c.name]: { ...pref, included: e.target.checked } })}
                      className="w-4 h-4 accent-[#188038]"
                      data-testid="v1-channel-include-checkbox"
                    />
                    <input
                      value={pref.alias !== undefined ? pref.alias : c.name}
                      onChange={(e) => setChannelPrefs({ ...channelPrefs, [c.name]: { ...pref, alias: e.target.value } })}
                      className="flex-1 text-sm font-semibold border border-transparent hover:border-gray-200 focus:border-green-700 rounded-lg px-2 py-1 focus:outline-none"
                      data-testid="v1-channel-alias-input"
                    />
                    {pref.alias !== undefined && pref.alias.trim() && pref.alias.trim() !== c.name && (
                      <span className="text-[10px] text-gray-400">was: {c.name}</span>
                    )}
                    <span className="text-sm font-bold text-gray-600">{formatCompact(c.revenue, { isCurrency: true, currency: config.currency })}</span>
                  </div>
                );
              })}
            </div>
          ) : channels.length > 0 ? (
          <table className="w-full text-sm">
            <thead>
              <tr className="text-[11px] font-bold text-gray-500 uppercase">
                <th className="text-left px-5 py-2">Channel</th>
                <th className="text-right px-5 py-2">Revenue</th>
                <th className="text-right px-5 py-2">% Mix</th>
                <th className="text-right px-5 py-2">Sessions</th>
                <th className="text-right px-5 py-2">Rev MoM</th>
              </tr>
            </thead>
            <tbody>
              {channels.map((c) => (
                <tr key={c.name} className="border-t border-gray-100">
                  <td className="px-5 py-3 font-semibold">{c.name}</td>
                  <td className="px-5 py-3 text-right font-bold">{formatCompact(c.revenue, { isCurrency: true, currency: config.currency })}</td>
                  <td className="px-5 py-3 text-right text-gray-600">
                    {c.mix !== null ? `${c.mix.toFixed(1)}%` : "–"}
                    {c.mixShift !== null && <span className={`ml-1 text-[10px] font-bold ${c.mixShift >= 0 ? "text-[#137333]" : "text-[#C5221F]"}`}>({c.mixShift >= 0 ? "+" : ""}{c.mixShift.toFixed(1)}pt)</span>}
                  </td>
                  <td className="px-5 py-3 text-right text-gray-600">{c.sessions !== null ? formatNumber(c.sessions) : "–"}</td>
                  <td className="px-5 py-3 text-right"><DeltaPill value={c.revMom} /></td>
                </tr>
              ))}
            </tbody>
          </table>
          ) : (
            <div className="p-6 text-sm text-gray-500 text-center">All channels are excluded — re-include at least one via "Select / rename".</div>
          )}
        </div>
      )}

      {/* AI section */}
      <div className="v1-card mt-6 p-6" data-testid="v1-ai-section">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div>
            <div className="font-extrabold flex items-center gap-2">
              <Sparkles className="w-4 h-4" style={{ color: "#E37400" }} /> AI narrative
              <span className="px-2 py-0.5 rounded-full text-[10px] font-bold bg-[#F3E8FD] text-[#7C3AED]" data-testid="v1-claude-badge">Claude</span>
            </div>
            <div className="text-xs text-gray-500 mt-1">1. Analyze the data → 2. add your context → 3. generate. Edit anything after.</div>
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            <select value={ai.tone} onChange={(e) => setAi({ ...ai, tone: e.target.value })} className={selectCls} data-testid="v1-tone-select">
              {TONES.map((t) => <option key={t.value} value={t.value}>{t.label}</option>)}
            </select>
            <select value={ai.length} onChange={(e) => setAi({ ...ai, length: e.target.value })} className={selectCls} data-testid="v1-length-select">
              {LENGTHS.map((l) => <option key={l.value} value={l.value}>{l.label}</option>)}
            </select>
          </div>
        </div>

        {/* Stage 1 — Data findings */}
        <div className="mt-5 p-4 rounded-xl border border-gray-200 bg-[#FDFCF9]" data-testid="v1-findings-block">
          <div className="flex flex-wrap items-center justify-between gap-2">
            <div>
              <div className="text-sm font-extrabold flex items-center gap-2"><FlaskConical className="w-4 h-4" style={{ color: "#188038" }} /> Data findings</div>
              <div className="text-[11px] text-gray-500 mt-0.5">Verified facts computed from your files (revenue bridge, channel drivers, mix shifts). Edit or delete anything before it feeds the narrative.</div>
            </div>
            <button onClick={analyzeData} disabled={analyzing || generating || !kpiCurrent} className="v1-btn-ghost flex items-center gap-2 !py-2 text-sm" data-testid="v1-analyze-button">
              {analyzing ? <><Loader2 className="w-4 h-4 animate-spin" /> Analyzing…</> : ai.findings ? "Re-analyze" : "Analyze data"}
            </button>
          </div>
          <textarea
            value={ai.findings}
            onChange={(e) => setAi({ ...ai, findings: e.target.value })}
            rows={ai.findings ? 7 : 3}
            placeholder="Click 'Analyze data' — the engine computes the month's verified movements and Claude turns them into findings you can review."
            className="w-full mt-3 border border-gray-200 rounded-xl p-3 text-xs leading-relaxed focus:outline-none focus:border-green-700 bg-white"
            data-testid="v1-findings-textarea"
          />
        </div>

        {/* Stage 2 — Instructions (shared) & per-section context */}
        <button
          onClick={() => setInstOpen(!instOpen)}
          className="mt-4 w-full flex items-center justify-between px-4 py-2.5 rounded-xl border border-gray-200 bg-[#FDFCF9] text-sm font-bold text-gray-700 hover:border-gray-400 transition-colors"
          data-testid="v1-instructions-toggle"
        >
          <span className="flex items-center gap-2">
            <SlidersHorizontal className="w-4 h-4" style={{ color: "#B06000" }} />
            Instructions & context
            {ai.instructions.trim() && <span className="px-2 py-0.5 rounded-full text-[10px] font-bold bg-[#E6F4EA] text-[#137333]">{ai.skillName ? `using "${ai.skillName}"` : "custom instructions active"}</span>}
            {(ai.summaryContext.trim() || ai.insightsContext.trim()) && <span className="px-2 py-0.5 rounded-full text-[10px] font-bold bg-[#E8F0FE] text-[#1A73E8]">context added</span>}
          </span>
          <ChevronDown className={`w-4 h-4 transition-transform ${instOpen ? "rotate-180" : ""}`} />
        </button>
        {instOpen && (
          <div className="grid sm:grid-cols-3 gap-4 mt-3 v1-fade-in" data-testid="v1-instructions-panel">
            <div>
              <div className="flex items-center justify-between">
                <label className="text-[11px] font-bold text-gray-500 uppercase tracking-wide">Instructions (both sections)</label>
                {skills.length > 0 && (
                  <select
                    value=""
                    onChange={(e) => {
                      const s = skills.find((sk) => sk.skill_id === e.target.value);
                      if (s) setAi({ ...ai, skillName: s.name, instructions: s.instructions });
                    }}
                    className="text-[11px] border border-gray-300 rounded-full px-2 py-1 bg-white font-semibold"
                    data-testid="v1-skill-select"
                  >
                    <option value="" disabled>Load skill…</option>
                    {skills.map((s) => <option key={s.skill_id} value={s.skill_id}>{s.name}</option>)}
                  </select>
                )}
              </div>
              <textarea
                value={ai.instructions}
                onChange={(e) => setAi({ ...ai, instructions: e.target.value, skillName: "" })}
                rows={5}
                placeholder="How to write — e.g. You are a senior digital analytics expert. Always tie findings to dealer network impact…"
                className="w-full mt-1.5 border border-gray-200 rounded-xl p-3 text-xs leading-relaxed focus:outline-none focus:border-green-700"
                data-testid="v1-instructions-textarea"
              />
            </div>
            <div>
              <label className="text-[11px] font-bold text-gray-500 uppercase tracking-wide">Summary context</label>
              <textarea
                value={ai.summaryContext}
                onChange={(e) => setAi({ ...ai, summaryContext: e.target.value })}
                rows={5}
                placeholder="What the executive summary should reflect — e.g. Black Friday campaign ran Nov 20–30; keep board-friendly"
                className="w-full mt-1.5 border border-gray-200 rounded-xl p-3 text-xs leading-relaxed focus:outline-none focus:border-green-700"
                data-testid="v1-summary-context-textarea"
              />
            </div>
            <div>
              <label className="text-[11px] font-bold text-gray-500 uppercase tracking-wide">Insights context</label>
              <textarea
                value={ai.insightsContext}
                onChange={(e) => setAi({ ...ai, insightsContext: e.target.value })}
                rows={5}
                placeholder="What the strategic insights should dig into — e.g. explore why paid CVR dropped; dealer stocking season starts Dec"
                className="w-full mt-1.5 border border-gray-200 rounded-xl p-3 text-xs leading-relaxed focus:outline-none focus:border-green-700"
                data-testid="v1-insights-context-textarea"
              />
            </div>
          </div>
        )}

        {/* Stage 3 — Generate */}
        <div className="flex justify-end mt-4">
          <button onClick={generateNarrative} disabled={generating || analyzing || !kpiCurrent} className="v1-btn-primary flex items-center gap-2" data-testid="v1-generate-insights-button">
            {generating ? <><Loader2 className="w-4 h-4 animate-spin" /> Writing…</> : <>Generate narrative</>}
          </button>
        </div>
        <div className="grid sm:grid-cols-2 gap-4 mt-4">
          <div>
            <label className="text-[11px] font-bold text-gray-500 uppercase tracking-wide">Executive summary</label>
            <textarea value={ai.summary} onChange={(e) => setAi({ ...ai, summary: e.target.value })} rows={6}
              placeholder="Generated summary appears here — or write your own"
              className="w-full mt-1.5 border border-gray-200 rounded-xl p-3 text-sm leading-relaxed focus:outline-none focus:border-green-700"
              data-testid="v1-summary-textarea" />
          </div>
          <div>
            <label className="text-[11px] font-bold text-gray-500 uppercase tracking-wide">Strategic insights</label>
            <textarea value={ai.insights} onChange={(e) => setAi({ ...ai, insights: e.target.value })} rows={6}
              placeholder="Generated insights & next steps appear here"
              className="w-full mt-1.5 border border-gray-200 rounded-xl p-3 text-sm leading-relaxed focus:outline-none focus:border-green-700"
              data-testid="v1-insights-textarea" />
          </div>
        </div>
      </div>

      <div className="mt-10 flex items-center justify-between">
        <button onClick={() => goTo(0)} className="v1-btn-ghost" data-testid="v1-configure-back-button">← Back</button>
        <button onClick={handleContinue} disabled={!kpiCurrent || generating} className="v1-btn-primary flex items-center gap-2" data-testid="v1-configure-continue-button">
          {generating ? <><Loader2 className="w-4 h-4 animate-spin" /> Preparing…</> : <>Preview report →</>}
        </button>
      </div>
    </div>
  );
}
